import { BaseRequest, BaseResponse, MID } from './types'
import { resolveDeviceName } from './device-model-map'

const textDecoderUtf8 = new TextDecoder('utf-8')

function asView(data: Uint8Array): DataView {
  return new DataView(data.buffer, data.byteOffset, data.byteLength)
}

function readUInt8(data: Uint8Array, offset: number): number {
  return data[offset]
}

function readUInt16BE(data: Uint8Array, offset: number): number {
  return asView(data).getUint16(offset, false)
}

function readInt16BE(data: Uint8Array, offset: number): number {
  return asView(data).getInt16(offset, false)
}

function readUInt32LE(data: Uint8Array, offset: number): number {
  return asView(data).getUint32(offset, true)
}

function decodeUtf8(data: Uint8Array, start: number, end: number): string {
  return textDecoderUtf8.decode(data.subarray(start, end))
}

function bytesToHexSpaced(data?: Uint8Array): string {
  if (!data) return ''
  let str = ''
  for (let i = 0; i < data.length; i++) {
    str += data[i].toString(16).padStart(2, '0')
    if (i < data.length - 1) str += ' '
  }
  return str
}

export function getChecksum(data: Uint8Array): number {
  let checksum = 0
  if (data.length > 0) {
    checksum = data[0]
  }
  for (let i = 1; i < data.length; ++i) {
    checksum ^= data[i]
  }
  return checksum
}

export function getKeyByRandom(random: Uint8Array): Uint8Array | undefined {
  if (random.length !== 16) {
    return
  }

  let TempA = 0
  let i
  TempA = random[3] * random[3]
  TempA = TempA + random[7] * random[7]
  TempA = TempA + random[11] * random[11]
  TempA = TempA + random[15] * random[15]
  TempA = TempA + random[0]
  TempA = TempA + random[4]
  TempA = TempA + random[7]
  TempA = TempA + 0x83b3
  TempA = TempA & 0xfffff

  const key = new Uint8Array(16)

  key[0] = (TempA >> 12) ^ 0x05
  key[1] = (TempA >> 4) ^ 0x07
  key[2] = ((TempA << 4) & (TempA >> 16)) ^ 0x08
  key[3] = (TempA >> 8) ^ 0x09
  key[4] = TempA ^ 0x10

  key[5] = (TempA >> 12) ^ 0x20
  key[6] = (TempA >> 4) ^ 0x23
  key[7] = ((TempA << 4) & (TempA >> 16)) ^ 0x24
  key[8] = (TempA >> 8) ^ 0x25
  key[9] = TempA ^ 0x26

  key[10] = (TempA >> 12) ^ 0x28
  key[11] = (TempA >> 4) ^ 0x29
  key[12] = ((TempA << 4) & (TempA >> 16)) ^ 0x30
  key[13] = (TempA >> 8) ^ 0x31
  key[14] = TempA ^ 0x32
  key[15] = TempA ^ 0x33

  for (i = 0; i < 16; i++) {
    key[i] = random[i] ^ key[i]
  }

  return key
}

export function formatMid(mid: Uint8Array): string {
  if (mid.length !== 2) {
    throw new Error('Buffer must be exactly 2 bytes long')
  }

  const binaryString =
    mid[0].toString(2).padStart(8, '0') + mid[1].toString(2).padStart(8, '0')

  const last15Bits = binaryString.slice(-15)

  const segments = [
    last15Bits.slice(0, 5),
    last15Bits.slice(5, 10),
    last15Bits.slice(10, 15),
  ]

  const letters = segments.map((segment) => {
    const letter = MID[segment]
    if (!letter) {
      throw new Error(`Invalid binary segment: ${segment}`)
    }
    return letter
  })

  return letters.join('')
}

export function mV2V(mV: number): number {
  return Math.round((mV / 1000) * 10) / 10
}

export function mA2A(mA: number): number {
  return Math.round((mA / 1000) * 10) / 10
}

export function mW2W(mW: number): number {
  return Math.round((mW / 1000) * 10) / 10
}

const protocolMap: { [key: number]: string } = {
  0: 'NONE',
  1: 'QC2.0',
  2: 'QC3.0',
  3: 'QC3+',
  4: 'SFCP',
  5: 'AFC',
  6: 'FCP',
  7: 'SCP',
  8: 'VOOC1.0',
  9: 'VOOC4.0',
  10: 'SupperVooc2.0',
  11: 'TFCP',
  12: 'UFCS',
  13: 'PE1.0',
  14: 'PE2.0',
  15: 'PD fix 5v',
  16: 'PD fix high voltage',
  17: 'PD SPR AVS',
  18: 'PD PPS',
  19: 'PD EPR high voltage',
  20: 'PD EPR AVS',
}

const bandMap: { [key: number]: string } = {
  0: '0 Mbps',
  1: '10 Mbps',
  2: '100 Mbps',
  3: '1 Gbps',
  4: '2.5 Gbps',
}

const rateMap: { [key: number]: string } = {
  0: 'none',
  1: '1G62',
  2: '2G7',
  3: '5G4',
  4: '8G1',
}

const ufpStatusMap: { [key: number]: string } = {
  0: 'disconnected',
  1: 'LS',
  2: 'FS',
  3: 'HS',
  4: 'SS',
  5: 'SS+',
}

const hostDriverMap: { [key: number]: string } = {
  0: 'host driver not present',
  1: 'host driver present, but not connected',
  2: 'host driver present and connected',
}

export class Praser {
  static readonly PAYLOAD_MAX_LENGTH = 58
  static readonly MIN_LENGTH = 6
  static readonly MAX_LENGTH = 64
  static readonly REPORT_ID = 0x95

  static ResponseFromByteArray(rdata: Uint8Array): BaseResponse {
    if (rdata.length < Praser.MIN_LENGTH || rdata[0] !== Praser.REPORT_ID) {
      throw new Error('read data error')
    }
    const fid = rdata[1]
    const cmd = rdata[2]
    const packetIndex = rdata[3]
    const length = rdata[4] - Praser.MIN_LENGTH
    const payload = rdata.subarray(5, 5 + length)

    const array = rdata.subarray(0, rdata[4] - 1)
    const checksum = getChecksum(array)
    if (checksum !== rdata[rdata[4] - 1]) {
      throw new Error(
        `checksum error,read:${rdata[rdata[4] - 1]},calc:${checksum}`
      )
    }
    return { packetCountdown: packetIndex, fid, cmd, data: payload }
  }

  static RequestToByteArray(req: BaseRequest): Array<Uint8Array> {
    const { data, fid } = req
    const buffers: Uint8Array[] = []
    if (req.data.length <= Praser.PAYLOAD_MAX_LENGTH) {
      const _wd = new Uint8Array(Praser.MAX_LENGTH)
      _wd[0] = Praser.REPORT_ID
      _wd[1] = fid || 0x00
      _wd[2] = req.requestCmd
      _wd[3] = 0
      _wd[4] = Praser.MIN_LENGTH + data.length
      _wd.set(data, 5)

      _wd[_wd[4] - 1] = getChecksum(_wd.subarray(0, _wd[4] - 1)) & 0xff
      buffers.push(_wd)
    } else {
      const ds: Uint8Array[] = []

      let packageCount = 0
      packageCount = Math.floor(data.length / Praser.PAYLOAD_MAX_LENGTH)
      for (let i = 0; i < packageCount; ++i) {
        const array = data.subarray(
          Praser.PAYLOAD_MAX_LENGTH * i,
          Praser.PAYLOAD_MAX_LENGTH * (i + 1)
        )
        ds.push(array)
      }

      if (data.length % Praser.PAYLOAD_MAX_LENGTH > 0) {
        const array = data.subarray(
          Praser.PAYLOAD_MAX_LENGTH * packageCount,
          Praser.PAYLOAD_MAX_LENGTH * packageCount +
            (data.length % Praser.PAYLOAD_MAX_LENGTH)
        )
        ds.push(array)
        packageCount += 1
      }

      for (let i = 0; i < ds.length; ++i) {
        const _wd = new Uint8Array(Praser.MAX_LENGTH)

        const _xx = ds[i]
        _wd[0] = Praser.REPORT_ID
        _wd[1] = 0x00
        _wd[2] = req.requestCmd

        let countdown = ds.length - 1 - i
        if (countdown > 0xff) {
          countdown = 0xff
        }
        _wd[3] = countdown

        _wd[4] = Praser.MIN_LENGTH + _xx.length

        _wd.set(_xx, 5)

        _wd[_wd[4] - 1] = getChecksum(_wd.subarray(0, _wd[4] - 1)) & 0xff
        buffers.push(_wd)
      }
      return buffers
    }

    return buffers
  }

  static FormatUsbcData(data: Uint8Array): any {
    const voltage = readUInt16BE(data, 3)
    const current = readUInt16BE(data, 5)
    return {
      status: readUInt8(data, 0),
      chargingProtocol: protocolMap[readUInt8(data, 1)],
      power: mW2W((voltage * current) / 1000),
      voltage: mV2V(voltage),
      current: mA2A(current),
    }
  }

  static calculateMonitorSizeCm(widthCm: number, heightCm: number): number {
    const widthMm = widthCm * 10
    const heightMm = heightCm * 10
    const diagonalMm = Math.sqrt(widthMm ** 2 + heightMm ** 2)
    const diagonalInches = diagonalMm / 25.4
    return Math.round(diagonalInches)
  }

  static FormatHDMIDisplayData(data: Uint8Array): any {
    const status = readUInt8(data, 0)
    if (!status) return null
    const flags = readUInt8(data, 31)
    const ChannelZeroErrorCount = readUInt16BE(data, 32)
    const ChannelOneErrorCount = readUInt16BE(data, 34)
    const ChannelTwoErrorCount = readUInt16BE(data, 36)

    const scramblerStatus = flags & 0x01 ? 'enable' : 'disable'
    const clockStatus = flags & 0x02 ? 'clock detected' : 'clock not detected'
    const channel0LockStatus = flags & 0x04 ? 'lock' : 'unlock'
    const channel1LockStatus = flags & 0x08 ? 'lock' : 'unlock'
    const channel2LockStatus = flags & 0x10 ? 'lock' : 'unlock'

    const parseErrorCount = (count: number) => {
      const isValid = (count & 0x8000) !== 0
      const value = count & 0x7fff
      return {
        valid: isValid,
        count: value,
      }
    }

    return {
      status: readUInt8(data, 0),
      id: formatMid(new Uint8Array([data[2], data[1]])),
      productCode: bytesToHexSpaced(data.subarray(3, 5))
        .toLocaleUpperCase()
        .replace(/ /g, ''),
      resolution: `${readUInt16BE(data, 5)}*${readUInt16BE(data, 7)}`,
      originResolution: `${readUInt16BE(data, 10)}*${readUInt16BE(data, 12)}`,
      originRefreshRate: readUInt16BE(data, 14) * 0.01,
      size: Praser.calculateMonitorSizeCm(
        readUInt8(data, 16),
        readUInt8(data, 17)
      ),
      displayName: decodeUtf8(data, 18, 31),
      scramblerStatus,
      clockStatus,
      channel0LockStatus,
      channel1LockStatus,
      channel2LockStatus,
      channel0ErrorCount: parseErrorCount(ChannelZeroErrorCount),
      channel1ErrorCount: parseErrorCount(ChannelOneErrorCount),
      channel2ErrorCount: parseErrorCount(ChannelTwoErrorCount),
      refreshRate: (() => {
        try {
          return readUInt16BE(data, 38)
        } catch {
          return readUInt8(data, 9)
        }
      })(),
    }
  }

  static FormatDPDisplayData(data: Uint8Array): any {
    const status = readUInt8(data, 0)
    if (!status) return null
    const laneCountdata = readUInt8(data, 31)
    const linkRatedata = readUInt8(data, 32)
    const parseLaneCount = (count: number): string => {
      if (count === 0) return 'none'
      if (count >= 1 && count <= 4) return count.toString()
      return 'invalid'
    }
    const parseLinkRate = (rate: number): string => {
      return rateMap[rate] || 'invalid'
    }
    const laneCount = parseLaneCount(laneCountdata)
    const linkRate = parseLinkRate(linkRatedata)

    return {
      status: readUInt8(data, 0),
      id: formatMid(new Uint8Array([data[2], data[1]])),
      productCode: bytesToHexSpaced(data.subarray(3, 5))
        .toLocaleUpperCase()
        .replace(/ /g, ''),
      resolution: `${readUInt16BE(data, 5)}*${readUInt16BE(data, 7)}`,
      originResolution: `${readUInt16BE(data, 10)}*${readUInt16BE(data, 12)}`,
      originRefreshRate: readUInt16BE(data, 14) * 0.01,
      size: Praser.calculateMonitorSizeCm(
        readUInt8(data, 16),
        readUInt8(data, 17)
      ),
      displayName: decodeUtf8(data, 18, 31),
      laneCount,
      linkRate,
      refreshRate: (() => {
        try {
          return readUInt16BE(data, 33)
        } catch {
          return readUInt8(data, 9)
        }
      })(),
    }
  }

  static FormatUfpData(data: Uint8Array): any {
    const ufp = readUInt8(data, 0)
    return {
      ufpStatus: ufpStatusMap[ufp],
    }
  }

  static FormatHostDriverData(data: Uint8Array): any {
    const hoststatus = readUInt8(data, 0)
    return {
      hostDriverData: hostDriverMap[hoststatus],
    }
  }

  static FormatEthData(data: Uint8Array): any {
    return {
      status: readUInt8(data, 0),
      maxBandwidth: bandMap[readUInt8(data, 1)],
    }
  }

  static FormatErrorData(data: Uint8Array): any {
    const errorCount = readUInt8(data, 0)
    const errors: any = []
    for (let i = 1; i < data.length; i += 5) {
      const code = data[i]
      const errorInfo = readUInt32LE(data, i + 1)
      const error: number[] = []
      for (let bit = 0; bit < 32; bit++) {
        if (errorInfo & (1 << bit)) {
          error.push(bit)
        }
      }

      errors.push({ code, error })
    }
    return { errorCount, errors }
  }

  static FormatPdAdcData(data: Uint8Array): any {
    if (!data || data.length < 6 || data[0] !== 0x0a || data[1] !== 0x36) {
      return null
    }

    const voltageMv = readUInt16BE(data, 2)
    const currentMa = readInt16BE(data, 4)
    const hasValue = voltageMv > 0
    const powerMw = hasValue ? (voltageMv * currentMa) / 1000 : 0
    const fmtV = (mv: number) => (mv / 1000).toFixed(1) + 'V'
    const fmtA = (ma: number) =>
      (ma >= 0 ? '+' : '-') + (Math.abs(ma) / 1000).toFixed(3) + 'A'
    // 与 ADM 桌面端 FormatPdAdcData 一致：PD ADC 电流为有符号值，方向为负时取绝对值展示，
    // 并向下取整到 0.1W，避免 web 显示 "-4.6W" 而 App/设备端显示 "4.6W"
    const fmtW = (mw: number) =>
      (Math.floor(Math.abs(mw) / 100) / 10).toFixed(1) + 'W'

    return {
      voltageMv,
      currentMa,
      powerMw,
      voltageV: hasValue ? fmtV(voltageMv) : '--',
      currentA: hasValue ? fmtA(currentMa) : '--',
      powerW: hasValue ? fmtW(powerMw) : '--',
    }
  }

  /**
   * 同帧电源快照（请求 0x0239 / 响应 0x0A39，16B，对齐 wilwei 自测页）：
   * [2..3] UFP ADC V(mV)  [4..5] UFP ADC I(mA，有符号)
   * [6..7] PD ADC V(mV)   [8..9] PD ADC I(mA，有符号)
   * [10..11] PD-IN V(mV)  [12..13] PD-IN I(mA)
   * [14] UFP 协商 V(100mV/LSB)  [15] UFP 协商 I(50mA/LSB)
   * 各路电参同帧采样；pdAdc 分量与 FormatPdAdcData 同构，可直接喂旧缓存出口。
   */
  static FormatPowerSnapshotData(data: Uint8Array): any {
    if (!data || data.length < 16 || data[0] !== 0x0a || data[1] !== 0x39) {
      return null
    }

    const fmtV = (mv: number) => (mv / 1000).toFixed(1) + 'V'
    const fmtA = (ma: number) =>
      (ma >= 0 ? '+' : '-') + (Math.abs(ma) / 1000).toFixed(3) + 'A'
    const fmtW = (mw: number) =>
      (Math.floor(Math.abs(mw) / 100) / 10).toFixed(1) + 'W'
    const channel = (voltageMv: number, currentMa: number) => {
      const hasValue = voltageMv > 0
      const powerMw = hasValue ? (voltageMv * currentMa) / 1000 : 0
      return {
        voltageMv,
        currentMa,
        powerMw,
        voltageV: hasValue ? fmtV(voltageMv) : '--',
        currentA: hasValue ? fmtA(currentMa) : '--',
        powerW: hasValue ? fmtW(powerMw) : '--',
      }
    }

    return {
      ufpAdc: channel(readUInt16BE(data, 2), readInt16BE(data, 4)),
      pdAdc: channel(readUInt16BE(data, 6), readInt16BE(data, 8)),
      pdIn: channel(readUInt16BE(data, 10), readUInt16BE(data, 12)),
      ufpNeg: channel((data[14] || 0) * 100, (data[15] || 0) * 50),
    }
  }

  static FormatScreenSizeData(data: Uint8Array): any {
    if (!data || data.length < 9 || data[0] !== 0x0a || data[1] !== 0x27) {
      return null
    }

    const flag = data[2] || 0
    const formatInch = (bit: number, offset: number) => {
      if (!(flag & bit)) return ''
      const x10 = readUInt16BE(data, offset)
      if (x10 <= 0 || x10 > 2000) return ''
      return `${(Math.round(x10) / 10).toFixed(1)}″`
    }

    return {
      dpScreenSize: formatInch(0x01, 3),
      hdmiScreenSize: formatInch(0x02, 6),
      dpSizeCm: flag & 0x01 ? data[5] || 0 : 0,
      hdmiSizeCm: flag & 0x02 ? data[8] || 0 : 0,
    }
  }

  static FormatPortInfoData(data: Uint8Array): any {
    // 0x0A00 端口信息：64 字节完整包（含 0x0A 0x00 头部）
    // [0-1]   = 0x0A 0x00  响应头
    // [3]     = flags: bit0=eth已连接, bit1=PD-IN已连接, bit2=UFP已连接
    // [4-5]   = vendor_index, [6-7] = product_index
    // [8-12]  = SD/TF, C1, A1, A2, A3 speeds
    // [13-17] = DP(TX1): H/V/Hz, [18-22] = HDMI(TX2): H/V/Hz
    // [23]    = EDID flags
    // [24-33] = TX1/TX2 EDID, [34-53] = TX1/TX2 displayName
    // [54-57] = UFP V/I, [58-61] = PD-IN V/I, [62-63] = 上游 DP 版本 / link_rate

    const parseSpeedInfo = (offset: number) => {
      try {
        const val = readUInt8(data, offset)
        if (val === 0) return { status: 0, speed: 0, speedText: '--' }
        let speedText = 'Unknown'
        if (val & 4) speedText = '10 Gbps'
        else if (val & 2) speedText = '5 Gbps'
        else if (val & 1) speedText = '480 Mbps'
        else speedText = '0x' + val.toString(16)

        return { status: 1, speed: val, speedText }
      } catch {
        return { status: 0, speed: 0, speedText: '--' }
      }
    }

    const parseDisplayInfo = (
      baseOffset: number,
      edidBase: number,
      flagBit: number,
      nameOffset: number
    ) => {
      try {
        if (data.length < baseOffset + 5) return { connected: false }
        const h = readUInt16BE(data, baseOffset)
        const v = readUInt16BE(data, baseOffset + 2)
        const ref = readUInt8(data, baseOffset + 4)
        const valid = h > 0 && h <= 7680 && v > 0 && v <= 4320
        if (!valid) return { connected: false }

        let displayId = ''
        let productCode = ''
        let displayName = '--'
        const edidFlags = data[23] || 0
        if (edidFlags & flagBit && data.length >= edidBase + 5) {
          const m0 = data[edidBase] || 0
          const m1 = data[edidBase + 1] || 0
          const m2 = data[edidBase + 2] || 0
          let mfg = '--'
          if (
            m0 > 0x20 &&
            m0 < 0x7f &&
            m1 > 0x20 &&
            m1 < 0x7f &&
            m2 > 0x20 &&
            m2 < 0x7f
          ) {
            mfg = String.fromCharCode(m0, m1, m2)
          }
          const pCode = (data[edidBase + 3] << 8) | data[edidBase + 4]
          productCode = pCode.toString(16).toUpperCase().padStart(4, '0')
          displayId = mfg + productCode

          if (data.length >= nameOffset + 10) {
            const nameBuf = data.subarray(nameOffset, nameOffset + 10)
            let rawName = ''
            for (let i = 0; i < 10; i++) {
              const b = nameBuf[i] || 0
              if (b === 0) break
              if (b >= 0x20 && b < 0x7f) rawName += String.fromCharCode(b)
            }

            if (rawName.length > 0) {
              displayName = rawName
            } else if (pCode > 0) {
              displayName = '厂商 ' + mfg + ' / 产品 ' + '0x' + productCode
            }
          } else if (pCode > 0) {
            displayName = '厂商 ' + mfg + ' / 产品 ' + '0x' + productCode
          }
        }

        const refStr = ref > 0 && ref <= 240 ? `${ref}` : '--'
        return {
          connected: true,
          resolution: `${h}×${v}`,
          refreshRate: refStr,
          displayName,
          displayId,
          productCode,
          screenSize: '',
          specResolution: `${h}*${v}`,
          specRefreshRate: refStr,
        }
      } catch {
        return { connected: false }
      }
    }

    const vendorIndex = data.length >= 6 ? readUInt16BE(data, 4) : 0
    const productIndex = data.length >= 8 ? readUInt16BE(data, 6) : 0
    const ufpDeviceName = resolveDeviceName(vendorIndex, productIndex)

    const flags = data[3] || 0

    const pdInLinked = !!(flags & 0x02)
    const ufpLinked = !!(flags & 0x04)

    const ufpVmv = data.length >= 56 ? readUInt16BE(data, 54) : 0
    const ufpIraw = data.length >= 58 ? readInt16BE(data, 56) : 0
    const pdInVmv = data.length >= 60 ? readUInt16BE(data, 58) : 0
    const pdInIma = data.length >= 62 ? readUInt16BE(data, 60) : 0

    const ufpPowerMw = (ufpVmv * Math.abs(ufpIraw)) / 1000
    const pdInPowerMw = (pdInVmv * pdInIma) / 1000

    const ufpNegV100mv = data.length >= 63 ? readUInt8(data, 62) : 0
    const ufpNegI50ma = data.length >= 64 ? readUInt8(data, 63) : 0
    const ufpNegVmv = ufpNegV100mv * 100
    const ufpNegIma = ufpNegI50ma * 50
    const ufpNegPwrMw = (ufpNegVmv * ufpNegIma) / 1000

    const fmtV = (mv: number) => (mv / 1000).toFixed(1) + 'V'
    const fmtA = (ma: number) => (Math.abs(ma) / 1000).toFixed(1) + 'A'
    const fmtW = (mw: number) => (Math.floor(mw / 100) / 10).toFixed(1) + 'W'

    const dpVer = data.length >= 63 ? readUInt8(data, 62) : 0
    const dpLr = data.length >= 64 ? readUInt8(data, 63) : 0
    let displayProtocol = '--'
    if (dpVer === 1) {
      switch (dpLr) {
        case 6:
        case 10:
          displayProtocol = 'DP 1.2'
          break
        case 20:
        case 30:
          displayProtocol = 'DP 1.4'
          break
        default:
          displayProtocol = 'DP 1.x'
          break
      }
    } else if (dpVer === 2) {
      displayProtocol = 'DP 2.0'
    }

    return {
      sdtfInfo: parseSpeedInfo(8),
      c1Info: parseSpeedInfo(9),
      a1Info: parseSpeedInfo(10),
      a2Info: parseSpeedInfo(11),
      a3Info: parseSpeedInfo(12),
      dpInfo: parseDisplayInfo(13, 24, 0x01, 34),
      hdmiInfo: parseDisplayInfo(18, 29, 0x02, 44),
      ethInfo: { linked: !!(flags & 0x01) },
      displayProtocol,
      pdInInfo: {
        linked: pdInLinked,
        voltageMv: pdInVmv,
        currentMa: pdInIma,
        powerMw: pdInPowerMw,
        voltageV: pdInLinked && pdInVmv > 0 ? fmtV(pdInVmv) : '--',
        currentA: pdInLinked && pdInIma > 0 ? fmtA(pdInIma) : '--',
        powerW: pdInLinked && pdInPowerMw > 0 ? fmtW(pdInPowerMw) : '--',
      },
      ufpInfo: {
        linked: ufpLinked,
        voltageMv: ufpVmv,
        currentMa: ufpIraw,
        powerMw: ufpPowerMw,
        voltageV: ufpLinked && ufpVmv > 0 ? fmtV(ufpVmv) : '--',
        currentA: ufpLinked && Math.abs(ufpIraw) > 0 ? fmtA(ufpIraw) : '--',
        powerW: ufpLinked && ufpPowerMw > 0 ? fmtW(ufpPowerMw) : '--',
        negPowerMw: ufpNegPwrMw,
        negPowerW: ufpNegPwrMw > 0 ? fmtW(ufpNegPwrMw) : '--',
        deviceName: ufpDeviceName,
        vendorIndex,
        productIndex,
      },
    }
  }

  static FormatFocusRecordData(data: Uint8Array): any {
    if (!data || data.length < 42 || data[0] !== 0x0a || data[1] !== 0x02)
      return null

    const u32le = (offset: number) =>
      (data[offset] || 0) |
      ((data[offset + 1] || 0) << 8) |
      ((data[offset + 2] || 0) << 16) |
      ((data[offset + 3] || 0) << 24)

    const enabled = !!data[2]
    const elapsedSec = u32le(6)
    const focusDurationSec = u32le(10)
    const totalDurationSec = u32le(14)
    const maxContinuousSec = u32le(18)
    const totalRecords = u32le(22)
    const cumulativeFocusSec = u32le(26)
    const cumulativeLeisureSec = u32le(30)
    const historicalMaxContinuousSec = u32le(34)

    const idleDurationSec = Math.max(0, totalDurationSec - focusDurationSec)

    return {
      enabled,
      isFocusing: elapsedSec > 0,
      elapsedSec,
      focusTotalSec: totalDurationSec,
      focusDurationSec,
      idleDurationSec,
      longestContinuousSec: maxContinuousSec,
      totalRecords,
      cumulativeFocusSec,
      cumulativeLeisureSec,
      historicalLongestSec: historicalMaxContinuousSec,
    }
  }

  /** 番茄/健康提醒族命令统一的 uint32 小端读取 */
  private static u32le(data: Uint8Array, offset: number): number {
    return (
      ((data[offset] || 0) |
        ((data[offset + 1] || 0) << 8) |
        ((data[offset + 2] || 0) << 16) |
        ((data[offset + 3] || 0) << 24)) >>>
      0
    )
  }

  /**
   * 解析专注/健康提醒参数 0x0A03（22 字节，注意响应头不是 0x0A0B）
   * [2-5]=breakResetSec [6-9]=notifyMinFocusSec [10-13]=timeScale
   * [14-17]=l1ToL2Sec（健康提醒循环间隔，默认 2700=45min）[18-21]=l2ToL3Sec（兼容保留）
   */
  static FormatFocusParamsData(data: Uint8Array): any {
    if (!data || data.length < 22 || data[0] !== 0x0a || data[1] !== 0x03) {
      return null
    }
    return {
      breakResetSec: Praser.u32le(data, 2),
      notifyMinFocusSec: Praser.u32le(data, 6),
      timeScale: Praser.u32le(data, 10),
      l1ToL2Sec: Praser.u32le(data, 14),
      l2ToL3Sec: Praser.u32le(data, 18),
    }
  }

  /** 解析番茄时长配置 0x0A2A（6 字节）→ 设备当前实际配置秒数 */
  static FormatTomatoDurationData(data: Uint8Array): number | null {
    if (!data || data.length < 6 || data[0] !== 0x0a || data[1] !== 0x2a) {
      return null
    }
    return Praser.u32le(data, 2)
  }

  /**
   * 解析番茄运行状态 0x0A2B（11 字节）
   * [2]=state 0=空闲 1=进入/倒计时中 2=已自然完成 3=正在退出
   * [3-6]=remainingSec [7-10]=completionSeq（每次自然归零递增，重启清零）
   */
  static FormatTomatoStatusData(data: Uint8Array): any {
    if (!data || data.length < 11 || data[0] !== 0x0a || data[1] !== 0x2b) {
      return null
    }
    return {
      state: data[2],
      remainingSec: Praser.u32le(data, 3),
      completionSeq: Praser.u32le(data, 7),
    }
  }

  /** 解析番茄累计 0x0A2D（10 字节）→ 自然完成次数 + 累计秒数 */
  static FormatTomatoStatsData(data: Uint8Array): any {
    if (!data || data.length < 10 || data[0] !== 0x0a || data[1] !== 0x2d) {
      return null
    }
    return {
      completedCount: Praser.u32le(data, 2),
      completedSec: Praser.u32le(data, 6),
    }
  }

  /** 解析健康提醒页显示时长 0x0A2E（6 字节）→ 秒数 */
  static FormatHealthReminderDurationData(data: Uint8Array): number | null {
    if (!data || data.length < 6 || data[0] !== 0x0a || data[1] !== 0x2e) {
      return null
    }
    return Praser.u32le(data, 2)
  }
}
